define((require) => {
    "use strict";

    const {
        invoke, extend, clone, result, defer
    }    = require("underscore");
    const AnimationTypes                            = require("shared/ui/experience/widgets/animations/AnimationTypes");
    const Browser                                   = require("shared/services/Browser");
    const Color                                     = require("shared/util/Color");
    const EffectController                          = require("shared/ui/experience/widgets/effects/EffectController");
    const Marionette                                = require("marionette");
    const MathUtil                                  = require("shared/util/MathUtil").default;
    const Promise                                   = require("shared/util/Promise");
    const {Logger}                                    = require("shared/util/Logger");
    const Rectangle                                 = require("shared/util/Rectangle");
    const TweenMax                                  = require("TweenMax");
    const WidgetScaleMode                           = require("shared/ui/experience/widgets/WidgetScaleMode");
    const {encapsulateInnerDom}                     = require("shared/util/DomUtil");

    /**
     * Base view for the rendering of widgets.
     *
     * @class AbstractWidgetView
     * @constructor
     *
     * @cfg {AbstractWidgetModel} model The widget being rendered.
     * @cfg {ExperienceViewModel|EditorViewModel} viewModel The view model corresponding to the context the widget is rendered in.
     * @cfg {boolean} [edited] If true, indicates the widget is being edited.
     * @cfg {boolean} [enableAnimations] If true, indicates animations are enabled on the widget.
     */
    return Marionette.ItemView.extend({

        className: "widget-component",

        _animControllers: null,

        _effectController: null,

        _startAnimationsPending: false,

        _rendered: false,

        /**
         * @private
         * First rendering always inactive by default, the current Scene will then trigger a re-render when
         * becoming active, notifying this all other recursively contained widgets. This ensures that all other
         * Scenes/Widgets are inactive by default - as they should be - when rendered the first time (e.g. on
         * an adjacent Scene).
         *
         * @see SceneViewer#_active
         */
        _active: null,

        /**
         * @private
         * A lazy initialized logger with a name corresponding to this widget, acquired via
         * <tt>log()</tt>.
         */
        _logger: null,

        template: () => (""),

        events: {
            // Do nothing, widget selection is handled by SceneEditorTransformer
            "click": () => {}
        },

        modelEvents: {
            "change": "render"
        },

        initialize() {
            this._active = false;

            // Invoke onInitialize hook, passing the jQuery wrapped element
            this.onInitialize(this.$el);

            if (this.model.get("isLocked")) {
                this.$el.addClass("is-locked");
            }
            if (this.model.get("isEditorHidden")) {
                this.$el.addClass("is-hidden");
            }
        },

        /**
         * @protected
         * Returns the logger associated with this Widget, lazily initialized it on first
         * request.
         *
         * @return {Logger} The logger to use for this widget; neveer falsey.
         */
        _log() {
            if (!this._logger) {
                this._logger = new Logger(this.__typeName);
            }
            return this._logger;
        },

        _viewModel() {
            this.viewModel = this.options.viewModel || this.viewScope.viewModel;
            return this.viewModel;
        },

        _experienceSize(scaled) {
            return this._viewModel().get("experience").getSize(scaled);
        },

        /**
         * @private
         * Invoked when this widget is clicked/tapped if and only if this widget has an action.
         */
        _activate() {
            if (this._active) {
                const viewModel = this._viewModel();
                // TODO: Revisit
                // The 'actions' array has been deprecated - we'll continue to be backwards compatible in the viewer (for now).
                if (this.model.has("actions")) {
                    this.model.get("actions").each((action) => {
                        action.activate(viewModel);
                    });
                }
                // Activate action...
                const action = this.model.get("action");
                if (action) {
                    if (this.model.has("uuid")) {
                        action.attributes.widgetId = this.model.get("uuid");
                    }
                    action.activate(viewModel);
                }
            }
        },

        _hasAnimations() {
            return !this.model.get("animations").isEmpty();
        },

        /**
         * Gets the bounding box of the widget's element.
         * If the <tt>global</tt> boolean is passed in as <tt>true</tt>, the coordinates
         * will be calculated according to the global window and not the scene.
         *
         * @param  {Boolean} global   Whether the coordinates should be relative to the <tt>Window</tt>.
         * @return {Rectangle}        The bounds.
         */
        getBoundingBox(global) {
            const rotation = this.model.get("rotation");
            let rect;

            if (global) {
                // Round bounding client rectangle long float to the integer pixels value
                rect = MathUtil.roundRect(this.el.getBoundingClientRect());
                rect.x = rect.left;
                rect.y = rect.top;
                return new Rectangle(rect);
            }

            rect = this.getRect();

            if (rotation !== 0) {
                const unscaledExperienceSize = this._experienceSize();
                const x = (this.model.get("scaledLeft") / 100) * unscaledExperienceSize.width;
                const y = (this.model.get("scaledTop") / 100) * unscaledExperienceSize.height;
                const {width, height} = rect;
                return new Rectangle(MathUtil.rotateRect({
                    x, y, width, height
                }, {x, y}, rotation));
            }
            // If no rotation is applied to the widget, simply return the persisted rectangle data
            return rect;
        },

        /**
         * Returns a rectangle representing the size and position of the widget.
         *
         * Note that the returned rectangle bounds does not consider rotation, see #getBoundingBox.
         *
         * @return {Rectangle} a rectangle representing the size and position of the widget.
         */
        getRect() {
            let width = 0;
            let height = 0;
            const unscaledExperienceSize = this._experienceSize();
            const scale = this.model.get("scale");
            const rotation = this.model.get("rotation");
            const scaleMode = this.model.get("scaleMode");
            const rect = {
                x: MathUtil.round((this.model.get("scaledLeft") / 100) * unscaledExperienceSize.width),
                y: MathUtil.round((this.model.get("scaledTop") / 100) * unscaledExperienceSize.height)
            };

            // Not all widget models contains dimensions (i.e. text based widgets may only contain width)
            if (this.model.has("width")) {
                width = MathUtil.round((this.model.get("scaledWidth") / 100) * unscaledExperienceSize.width);
            } else {
                // Let's ask jQuery then...
                width = this.$el.outerWidth();
            }

            if (this.model.has("height")) {
                height = MathUtil.round((this.model.get("scaledHeight") / 100) * unscaledExperienceSize.height);
            } else {
                height = this.$el.outerHeight();
            }

            rect.width = width;
            rect.height = height;

            if (!this.options.edited) {
                // When rotation is applied inside the editor, the pivot point is located at top left (0,0) - courtesy of free transform?
                // Inside the viewer context, we operate with a centered pivot point, aka. a transform origin of 50% 50% (.transform-origin(50% 50%))
                // So we need to compensate for that to render correctly...
                const rotatedPoint = MathUtil.rotatePoint(rect, {x: rect.x + (rect.width / 2), y: rect.y + (rect.height / 2)}, rotation);

                rect.x += rect.x - rotatedPoint.x;
                rect.y += rect.y - rotatedPoint.y;
            }

            rect.right = rect.x + rect.width;
            rect.bottom = rect.y + rect.height;

            // If we're "stretch scaling", compensate for the fact that scaling is done
            // from the center (transformOrigin 50% 50%) - and adjust the position.
            if (scaleMode === WidgetScaleMode.STRETCH) {
                const xf = rect.width - (rect.width * scale.x);
                const yf = rect.height - (rect.height * scale.y);

                rect.x -= xf * 0.5;
                rect.y -= yf * 0.5;
            }

            // For consistency...
            rect.top = rect.y;
            rect.left = rect.x;

            return new Rectangle(rect);
        },

        /**
         * Returns the parent container element, wrapped in a jQuery instance.
         *
         * @return {jQueryElement} the parent container element, wrapped in a jQuery instance.
         */
        getParentContainer() {
            return this.options.container;
        },

        setActive(active) {
            if (active === this._active) {
                return;
            }

            this._active = active;

            if (this._effectController) {
                this._effectController.setActive(active);
            }

            if (this._rendered) {
                this._triggerUpdateRendering(this.$el, this.getRect(), active, this.options.edited, undefined /* no model changes */);

                if (active) {
                    this._startAnimationsPending = false;
                    this.startAnimations();
                } else {
                    this.stopAnimations();
                }
            }
        },

        /**
         * Returns widget active state
         *
         * @return {Boolean} true if widget is active
         */
        isActive() {
            return this._active;
        },

        /**
         * Animates the widget by applying all of the animations defined on the model.
         *
         * @private
         */
        startAnimations() {
            // Wait for the animation controllers to be set up if this method is invoked before DOM rendering is complete
            if (!this._animControllers) {
                // Since the animation controllers aren't constructed, they will be at the initial position
                this._startAnimationsPending = true;

                return;
            }

            // Make sure the element is visible
            this.$el.css("visibility", "");

            // Start the animations
            let i = this._animControllers.length;
            let controller;

            // Animations with 'immediateRender' set to true, needs to be started at the end, see #onShow
            while (i--) {
                controller = this._animControllers[i];
                controller.start();
            }
        },

        /**
         * Stop the animations.
         *
         * @private
         */
        stopAnimations() {
            // Stop any ongoing animations
            if (this._animControllers) {
                let i = this._animControllers.length;
                let controller;

                // Animations with 'immediateRender' set to true, needs to be stopped first in order to preserve state, see #onShow
                while (i--) {
                    controller = this._animControllers[i];
                    controller.stop();
                }
            }
        },

        onClose() {
            // Cleanup
            this.$el.off();

            // Dispose animations, if any...
            invoke(this._animControllers, "close");

            if (this._effectController) {
                this._effectController.dispose();
            }
        },

        /**
         * Callback invoked during the <tt>initialize</tt> cycle.
         *
         * @param  {jQuery} element The associated DOM element wrapped in a <tt>jQuery</tt> instance.
         */
        // eslint-disable-next-line no-unused-vars
        onInitialize(element) {
        },

        /**
         * Tells the widget to update it's rendering.<p>
         *
         * This is an abstract method that must be implemented by it's subclass.
         *
         * @param {HTMLElement} element jQuery wrapped HTMLElement.
         * @param {Rectangle} bounds Rectangular object representing the bounds of the widget.
         * @param {Boolean} active Whether the widget is in it's active state or not.
         * @param {Boolean} edited Whether the widget is currently being edited.
         * @param {Object} [changedProperties] Map of the properties that have changed. If unspecified, consider all properties as changed.
         */
        // eslint-disable-next-line no-unused-vars
        updateRendering(element, bounds, active, edited, changedProperties) {
        },

        /**
         * @private
         *
         * Returns the default CSS to be applied initially. These styles may be overridden by subclasses.
         *
         * @return {Object} A hash of CSS values mapped to property names.
         */
        defaultCSS() {
            const css = {};

            // Handle any background color
            if (this.model.has("backgroundColor")) {
                const backgroundHex = this.model.get("backgroundColor");
                let backgroundOpacity = this.model.get("backgroundOpacity");

                if (typeof backgroundOpacity !== "number") {
                    backgroundOpacity = 1;
                }

                if (backgroundHex === "transparent" || backgroundHex === "") {
                    css.backgroundColor = "transparent";
                } else {
                    css.backgroundColor = Color.hexToRgba(backgroundHex, backgroundOpacity);
                }
            } else {
                css.background = "transparent";
            }

            return css;
        },

        /**
         * @private
         *
         * Updates the appearance/style of the widget - applying the constructed CSS to the
         * associated HTML element.
         *
         * @param  {Object} style Optional CSS object representing the default styles.
         */
        _updateAppearance(style) {
            const rect = this.getRect();
            const rotation = this.model.get("rotation") || 0;
            const scale = this.model.get("scale");
            const css = style || {};

            if (this.model.has("width")) {
                css.width = rect.width;
            }

            if (this.model.has("height")) {
                css.height = rect.height;
            }

            if (this.model.get("scaleMode") === WidgetScaleMode.STRETCH) {
                css.scaleX = scale.x;
                css.scaleY = scale.y;
            }

            if (!this.model.get("isVisible")) {
                css.display = "none";
            } else {
                css.display = "block";
            }

            extend(css, {
                x: rect.x,
                y: rect.y,
                rotation,
                // Leave some z-index room for static elements
                zIndex: css.zIndex ? css.zIndex : this.model.get("layerIndex") + 100,
                force3D: Browser.capabilities.is3dAnimationsSupported
            });

            // avoid infinite error triggering with bad data
            if (css.boxShadow === true) {
                delete css.boxShadow;
            }
            TweenMax.set(this.el, css);

            invoke(this._animControllers, "invalidate");
        },

        // TODO: The methods below seems to deal with editor specific logic - candidate for the/a Transformable widget?
        /**
         * @private
         *
         * The 'centeredPivotPoint' property is used to initially center a
         * widget around it's coordinates (i.e. when dropped from the editor interface).
         *
         * This property is not persisted, and removed (unset) once the widget has been
         * positioned.
         *
         * @see  #_validatePosition
         *
         * @return {Boolean} <tt>true</tt> if the widget should be centered, <tt>false</tt> if not.
         */
        _centeredPivotPoint() {
            return this.model.has("centeredPivotPoint") && this.model.get("centeredPivotPoint");
        },

        /**
         * Validates the widget position according to the 'centeredPivotPoint' property.
         */
        _validatePosition() {
            const rect = this.getRect();

            // Don't handle 'centeredPivotPoint' if the rect is 'invalid'
            if (rect.width === 0 && rect.height === 0) {
                return rect;
            }

            // If the coordinates are to the center of the widget, adjust them now
            if (this._centeredPivotPoint()) {
                const experienceSize = this._experienceSize();
                const widthDelta = (rect.width / experienceSize.width) * 50; // Multiplied by 100 for percent, then divided by 2
                const heightDelta = (rect.height / experienceSize.height) * 50;

                this.model.set({
                    left: this.model.get("left") - widthDelta,
                    top: this.model.get("top") - heightDelta
                });

                // We no longer needs the property
                this.model.unset("centeredPivotPoint", {silent: true});
            }
        },

        /**
         * @private
         *
         * Sets up the effect controller, responsible for managing the hover and loop
         * effects. Passing in the animation controllers to allow any needed coupling
         * between effects and transitions.
         */
        _setupEffects() {
            if (this.options.enableAnimations && !this._effectController) {
                this._effectController = new EffectController(this, this._animControllers);
                this._effectController.setActive(this._active);
            }
        },

        /**
         * @private
         *
         * Sets up the widget zIndex. Which turns it to be over others widgets and
         * over the experience editor shadow.
         * Also it adds the box shadow, to accent highlighted widget
         * It removes boxShadow back, if unhighlighted.
         * It didn't removes the zIndex, because it will be overwritten
         * inside _updateAppearance function
         *
         * For the widgets, which has specific requirements for highlight
         * the highlight can be extended (changed) inside widget.
         * Jsut define and implement `applyWidgetSpecificHighlight` method inside.
         * (like image - it absolutely positioned over it's widget container)
         * (like text or line - it has transparent background by default)
         */
        _updateHightlight() {
            // Update widget with corrected zIndex and border selection if highlighted
            if (this.model.get("highlighted")) {
                this._updateAppearance({
                    zIndex: 301, // From 101 starts widgets + layer index, so z-index should be calculated
                    boxShadow: "0px 0px 0px 3px #8c01ff"
                });
            } else if (this.model.get("overlapped")) {
                this._updateAppearance({
                    boxShadow: "0px 0px 0px 3px #8c01ff"
                });
            } else if (!this.model.get("borderWidth")) {
                // Remove border, if highlight is ended and there are no border applied
                this._updateAppearance({
                    boxShadow: "0px 0px 0px 0px transparent"
                });
            }
            if (typeof this.applyWidgetSpecificHighlight === "function") {
                this.applyWidgetSpecificHighlight();
            }
        },

        /**
         * Returns a <tt>Promise</tt> that represents the widget view's load state. Once
         * resolved, the widget is considered "loaded".
         *
         * The default implementation returns a resolved <tt>Promise</tt>. Widgets that rely
         * on external resources, e.g. image widgets, might wanna implement this method and
         * resolve the promise once the needed data has been fetched.
         *
         * @return {Promise} A <tt>Promise</tt> that represents the widget view's load state.
         */
        promise() {
            return Promise.resolved();
        },

        /**
         * @override
         *
         * Override to avoid some of the default rendering behavior provided by Marionette, i.e. we
         * don't want the HTML content to be cleared for each rendering cycle.
         *
         * @return {AbstractWidgetView} This instance.
         */
        render() {
            // Respect the Marionette life cycle
            this.isClosed = false;

            this.triggerMethod("before:render", this);
            this.triggerMethod("item:before:render", this);

            this.bindUIElements();

            this.triggerMethod("render", this);
            this.triggerMethod("item:rendered", this);

            const rect = this.getRect();
            // Clone the changed properties, as they might not be the same when our promise is resolved
            const changed = clone(this.model.changed);

            // Update the initial appearance applying the default CSS
            this._updateAppearance(this.defaultCSS());

            // Update TabOrder highlight
            this._updateHightlight();

            // Call updateRendering to notify subclasses to update their rendering
            this._triggerUpdateRendering(this.$el, rect, this._active, this.options.edited, this._rendered ? changed : undefined);

            // Enabled options if not being edited, if not explicitly turned off, and if we have any...
            const enableActions = !this.options.edited && this.options.enableActions && this.model.isInteractive(); // TODO: why do we not only test for actions here (but also effects)?

            // Manually register click listener for triggering actions. We don't wanna use the Backbone 'events' object
            // for the sake of subclasses, and sub-classes can still listen explicitly for (jQuery) "activate"
            // event if they do *not* use actions...
            if (enableActions) { // still allow clicks on non-activateable actions, so we can track it!
                this.$el.off("activate");
                this.$el
                    .on("activate", this._activate.bind(this))
                    .css("cursor",  this.model.hasActions() ? "pointer" : "auto");
            }

            // Initially hide the element - attempt to avoid flickering for elements (like text)
            // that requires a rendering cycle in the DOM before it's visual appearance is correct.
            if (this._centeredPivotPoint() || !this._rendered) {
                this.$el.css("visibility", "hidden");
            }

            if (this.model.get("isLocked")) {
                this.$el.addClass("is-locked");
            } else {
                this.$el.removeClass("is-locked");
            }

            if (this.model.get("isEditorHidden")) {
                this.$el.addClass("is-hidden");
            } else {
                this.$el.removeClass("is-hidden");
            }

            this.el.id = this.cid;

            this.el.setAttribute("data-contain-widget-uuid", this.model.get("uuid"));


            // Keep track of the rendering state
            this._rendered = true;
            return this;
        },

        /**
         * @private
         *
         * Apply an extra function if an {@link Action} is attached to the widget.
         */
        _injectActionElement() {
            const action = this.model.get("action");

            // In order to not give responsibility to the Action with the DOM,
            // we handle the DOM logic into this class.
            if (action &&
               (!this.options.edited) && this.options.enableActions) {
                const {element, isWrapper} = action.getElement(this.el, this.model.get("title"));

                // If and element is present, then we injected into the dom.
                if (element) {
                    // Wrap all of the HTMLElement of the Widget.
                    if (isWrapper) {
                        encapsulateInnerDom(this.el, element);
                    } else {
                        this.el.appendChild(element);
                    }
                }
            }
        },

        /**
         * Tells the widget to update it's rendering, and fire an event.<p>
         *
         * @fires updatedRendering
         *
         * @param {HTMLElement} element jQuery wrapped HTMLElement.
         * @param {Rectangle} bounds Rectangular object representing the bounds of the widget.
         * @param {Boolean} active Whether the widget is in it's active state or not.
         * @param {Boolean} edited Whether the widget is currently being edited.
         * @param {Object} [changedProperties] Map of the properties that have changed. If unspecified, consider all properties as changed.
         */
        _triggerUpdateRendering(element, bounds, active, edited, changedProperties) {
            this.updateRendering(element, bounds, active, edited, changedProperties);
            this._injectActionElement();
            this.triggerMethod("updated:rendering", this);
        },

        // Runs logic after the view is in the dom, so we can get correct bounds
        showView() {
            // Validate the position here where we know the size
            // Validate the position and appearance here where we know the size
            this._validatePosition();
            this._updateAppearance();

            // Setup animation controllers if needed
            if (this.options.enableAnimations && this._hasAnimations()) {
                // Sort all animations by delay, and make sure all initial animations are set to "render immediately"
                const sortedAnimationModels = this.model.get("animations").sortBy((animationModel) => animationModel.get("delay"));
                let minDelay = sortedAnimationModels[0].get("delay");

                this._animControllers = sortedAnimationModels.map((animationModel) => {
                    const AnimationController = AnimationTypes.fromString(animationModel.get("type")).animationControllerClass;
                    const delay = animationModel.get("delay");
                    const immediateRender = delay === minDelay;

                    minDelay = delay;

                    return new AnimationController({
                        animationModel,
                        widgetView: this,
                        unscaledExperienceSize: this._experienceSize(),
                        immediateRender,
                        positionOffset: result(this.options, "getPositionOffset") || {top: 0, left: 0}
                    });
                });

                // Start animations if needed
                if (this._active && this._startAnimationsPending) {
                    this._startAnimationsPending = false;
                    this.startAnimations();
                }
            }

            if (!this.options.edited && this.model.hasEffects()) {
                this._setupEffects();
            }

            // Make sure the element is visible at this point (see #render)
            this.$el.css("visibility", "");
        },

        onShow() {
            // Defer to ensure we have the correct bounds
            defer(this.showView.bind(this));
        }
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/AbstractWidgetView.js